/**
 * ConsentBanner — bottom-fixed, brand-consistent, dismissable.
 *
 * Shown once, when getConsent() === CONSENT_UNSET. Also reappears when the
 * user clicks "Cookie preferences" (resetConsent()) in the footer.
 *
 * Choices:
 *   • Accept  → setConsent("granted")  → TikTok Pixel loads + PageView fires
 *   • Decline → setConsent("denied")   → nothing loads
 *
 * The banner is intentionally NON-BLOCKING: it doesn't cover the fold or
 * block navigation. This preserves the launch-day paywall conversion rate
 * while still meeting the "explicit consent required" bar.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    CONSENT_GRANTED,
    CONSENT_DENIED,
    CONSENT_UNSET,
    getConsent,
    setConsent,
    onConsentChange,
} from "@/lib/consent";

export default function ConsentBanner() {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        // Sync open-state with the consent store — appears when unset,
        // hides on any decision, reappears if the user asks to change it.
        setVisible(getConsent() === CONSENT_UNSET);
        const off = onConsentChange((val) => setVisible(val === CONSENT_UNSET));
        return off;
    }, []);

    if (!visible) return null;

    return (
        <div
            role="dialog"
            aria-live="polite"
            aria-label="Cookie and analytics consent"
            data-testid="consent-banner"
            className="fixed bottom-0 inset-x-0 z-[9999] px-2 sm:px-3 pb-2 sm:pb-3 pointer-events-none"
        >
            <div className="pointer-events-auto mx-auto max-w-2xl rounded-xl bg-white/95 dark:bg-[hsl(152,15%,10%)]/95 backdrop-blur-md border border-paw-ink/10 dark:border-paw-bone/10 shadow-xl px-3 py-2.5 sm:px-4 sm:py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <p className="text-[12px] sm:text-[13px] leading-snug text-paw-ink dark:text-paw-bone flex-1 min-w-0">
                    🍪 Cookies help us improve PawLink.{" "}
                    <Link to="/privacy-policy" className="underline decoration-paw-terra/60 underline-offset-2">
                        Privacy Policy
                    </Link>
                    .
                </p>
                <div className="flex items-center gap-2 shrink-0">
                    <button
                        type="button"
                        onClick={() => setConsent(CONSENT_DENIED)}
                        data-testid="consent-decline"
                        className="inline-flex items-center justify-center rounded-full px-3 h-8 text-[12px] font-medium text-paw-ink/80 dark:text-paw-bone/80 border border-paw-ink/15 dark:border-paw-bone/20 hover:bg-paw-ink/5 dark:hover:bg-paw-bone/5 transition-colors"
                    >
                        Decline
                    </button>
                    <button
                        type="button"
                        onClick={() => setConsent(CONSENT_GRANTED)}
                        data-testid="consent-accept"
                        className="inline-flex items-center justify-center rounded-full px-4 h-8 text-[12px] font-semibold text-white bg-paw-terra hover:bg-paw-terra/90 shadow-sm shadow-paw-terra/20 transition-colors"
                    >
                        Accept
                    </button>
                </div>
            </div>
        </div>
    );
}
